import React from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import { GoSearch } from 'react-icons/go';
import { Ring } from 'react-awesome-spinners';
import palette from '../../lib/styles/palette';
import { timeValueToDate } from '../../lib/commonFunc/commonFunc';

const Wrapper = styled.div`
  margin-top: 24px;
  padding-bottom: 40px;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 220px;

  label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #3a4656;
    margin-bottom: 6px;
  }
`;

const KeywordField = styled(Field)`
  flex: 1;
  min-width: 260px;
`;

const KeywordInput = styled.input`
  height: 44px;
  border-radius: 8px;
  border: 1px solid #d7dbe2;
  padding: 0 14px;
  font-size: 0.95rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: ${palette.webCyan[5]};
    box-shadow: 0 0 0 3px rgba(26, 115, 233, 0.12);
  }
`;

const SearchButton = styled.button`
  height: 44px;
  padding: 0 22px;
  border: none;
  border-radius: 8px;
  background: ${palette.webCyan[5]};
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: ${palette.webCyan[6] || '#1b78f0'};
  }

  &:disabled {
    cursor: not-allowed;
    background: #c7d5ef;
    color: #fff;
  }
`;

const Summary = styled.div`
  margin-top: 18px;
  font-size: 0.92rem;
  color: #526174;

  strong {
    color: ${palette.webCyan[5]};
  }
`;

const ErrorMessage = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff5f5;
  color: #d63447;
  font-size: 0.9rem;
`;

const LoadingBox = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: center;
`;

const TableWrapper = styled.div`
  margin-top: 18px;
  border: 1px solid #e2e7ef;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
`;

const TableHeader = styled.div`
  padding: 16px 20px;
  background: #f7f9fc;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e8edf4;
  font-weight: 600;
  color: #1f3358;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead tr {
    background: #ffffff;
    border-bottom: 1px solid #eef2f6;
  }

  th {
    text-align: left;
    padding: 12px 18px;
    font-size: 0.85rem;
    font-weight: 600;
    color: #4a5568;
  }

  td {
    padding: 12px 18px;
    font-size: 0.9rem;
    color: #2d3748;
    border-bottom: 1px solid #f1f4f8;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const EmptyState = styled.div`
  margin-top: 40px;
  padding: 40px 0;
  text-align: center;
  color: #67778c;
  background: #f9fbff;
  border-radius: 12px;
  border: 1px dashed #d4deed;
`;

const SelectWrapper = styled.div`
  min-width: 220px;
`;

const PaginationWrapper = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  color: #4f5d72;
`;

const PaginationButton = styled.button`
  border: 1px solid #d7dbe2;
  background: #fff;
  color: #42506a;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f3f5f9;
  }

  &:disabled {
    cursor: not-allowed;
    color: #a5b1c3;
    border-color: #e3e8f0;
    background: #f7f9fc;
  }
`;

const PasswordButton = styled.button`
  padding: 4px 10px;
  border: 1px solid #d7dbe2;
  background: #fff;
  color: #42506a;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f3f5f9;
  }

  &:disabled {
    cursor: not-allowed;
    color: #a5b1c3;
    border-color: #e3e8f0;
    background: #f7f9fc;
  }
`;

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '44px',
    borderColor: state.isFocused ? palette.webCyan[5] : '#d7dbe2',
    boxShadow: 'none',
    '&:hover': {
      borderColor: palette.webCyan[5],
    },
    fontSize: '0.92rem',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 12px',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 15,
    fontSize: '0.9rem',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#eef4ff' : '#fff',
    color: '#1f2933',
  }),
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '-';
  const digits = phone.replace(/[^0-9]/g, '');
  if (digits.length < 4) return phone;
  if (digits.length < 8) return digits.replace(/(\d{3})(\d+)/, '$1-$2');
  if (digits.length === 9) return digits.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
  if (digits.length === 10) return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  return digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
};

const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  try {
    return timeValueToDate(timestamp);
  } catch (error) {
    return '-';
  }
};

const AdminClubSearch = ({
  keyword,
  onKeywordChange,
  onSearch,
  loading,
  members,
  totalCount,
  page,
  limit,
  clubOptions,
  selectedClub,
  onSelectClub,
  error,
  hasSearched,
  onPageChange,
  clubName,
  onCheckPassword = () => {},
  passwordLoading = false,
  onSelectMember = () => {},
}) => {
  const selectedOption =
    clubOptions.find((option) => option.value === selectedClub) || null;

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && onSearch) {
      onSearch();
    }
  };

  const totalPages = Math.max(Math.ceil(totalCount / limit), 1);

  const formatSex = (sex) => {
    if (sex === 1) return '남자';
    if (sex === 2) return '여자';
    return '-';
  };

  const formatPlatform = (platformType) => {
    switch (platformType) {
      case 13:
        return '이메일';
      case 14:
        return '카카오';
      case 17:
        return '애플';
      case null:
      case undefined:
        return '-';
      default:
        return '기타';
    }
  };
    
  return (
    <Wrapper>
      <Controls>
        <Field>
          <label>클럽 선택</label>
          <SelectWrapper>
            <Select
              styles={selectStyles}
              options={clubOptions}
              value={selectedOption}
              onChange={(option) => onSelectClub(option.value)}
              isSearchable
              placeholder="클럽을 선택하세요"
              isDisabled={!clubOptions.length}
            />
          </SelectWrapper>
        </Field>
        <KeywordField>
          <label>검색어</label>
          <KeywordInput
            placeholder="회원 이름, 연락처, 계정을 입력하세요"
            value={keyword}
            onChange={onKeywordChange}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
        </KeywordField>
        <SearchButton
          type="button"
          onClick={onSearch}
          disabled={loading || !selectedOption}
        >
          <GoSearch />
          검색
        </SearchButton>
      </Controls>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading && (
        <LoadingBox>
          <Ring />
        </LoadingBox>
      )}

      {!loading && !hasSearched && (
        <EmptyState>검색어를 입력하고 검색 버튼을 눌러 회원을 조회해 주세요.</EmptyState>
      )}

      {!loading && hasSearched && members.length === 0 && (
        <EmptyState>조건에 맞는 검색 결과가 없습니다.</EmptyState>
      )}

      {!loading && members.length > 0 && (
        <>
          {/* <Summary>
            <strong>{totalCount}명</strong>의 회원이 검색되었습니다.
          </Summary> */}
          <TableWrapper>
            <TableHeader>
              <span>{clubName || '선택된 클럽'}</span>
              <span>{page} / {totalPages} 페이지</span>
            </TableHeader>
            <Table>
              <thead>
                <tr>
                  <th>클럽</th>
                  <th>이름</th>
                  <th>성별</th>
                  <th>연락처</th>
                  <th>계정</th>
                  <th>플랫폼</th>
                  <th>가입일</th>
                  <th>회원번호</th>
                  <th>비밀번호</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr
                    key={`${member.playerID}-${member.account || member.phone}`}
                    onClick={() => onSelectMember(member)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{member.clubName || clubName || '-'}</td>
                    <td>{member.name || '-'}</td>
                    <td>{formatSex(member.sex)}</td>
                    <td>{formatPhoneNumber(member.phone)}</td>
                    <td>{member.account || '-'}</td>
                    <td>{formatPlatform(member.platformType)}</td>
                    <td>{formatDate(member.registTime)}</td>
                    <td>{member.playerID || '-'}</td>
                    <td>
                      <PasswordButton
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onCheckPassword && onCheckPassword(member);
                        }}
                        disabled={passwordLoading}
                      >
                        확인
                      </PasswordButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
          {totalPages > 1 && (
            <PaginationWrapper>
              <PaginationButton
                type="button"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1 || loading}
              >
                이전
              </PaginationButton>
              <span>
                {page} / {totalPages}
              </span>
              <PaginationButton
                type="button"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages || loading}
              >
                다음
              </PaginationButton>
            </PaginationWrapper>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default React.memo(AdminClubSearch);
